// The one place that decides the page returned to the foreground or left it (issue #4110). Live
// updates reconnect their socket and catch up, the screen wake lock asks for the lock again, push
// notifications re-register, and the page's audio clock checks itself for an iOS freeze (issue
// #4098), all on the signal from here, so a platform quirk handled here is handled for all of them.
//
// What counts:
//
// - `visibilitychange` on the document: a return when the page is visible, a departure otherwise.
//   It fires in browsers and in the store apps' webviews.
// - `pageshow` on the window: a return. visibilitychange alone is not enough: a back-forward cache
//   restore fires pageshow, and so does the iOS webview coming back to the foreground, which does
//   not always fire a visibilitychange.
// - The old iOS app builds' native signal, `receiveOnAppDidBecomeActive` (see registerNativeSignal).
// - `focus` on the window, only for a subscriber that asks for it (PageForegroundOptions.focus).
//
// A return arrives as a burst: on iOS, focus, visibilitychange and the native signal land within
// milliseconds of one another. The signal is not deduplicated, so a subscriber hears each of them;
// every subscriber tolerates a repeat: a repeated return does nothing new, and a repeated departure
// at most restarts a timer by the milliseconds between the two (live updates' give-up timer).
//
// Not every visibilitychange listener in the client goes through here yet: retries, the music cache
// refresh, the clock hooks, IndexedDB and the Learn and audio lab sessions still watch it on their
// own, for their own reasons. Moving them is a later issue, so a quirk fixed here does not reach them.

export interface PageForegroundOptions {
  // Also report a `focus` of the window as a return. A focus is not a foreground change by itself:
  // the window gains keyboard focus while already visible, which on a desktop happens at every click
  // into the window from another one. On iOS it is part of the return burst, though, so a subscriber
  // whose reaction is free and idempotent can take it as one more chance to notice a return (the
  // audio clock watchdog does). A focus never reports a departure, and it never changes what
  // isPageInForeground() reports either.
  focus?: boolean;
}

interface Subscriber {
  listener: (foreground: boolean) => void;
  focus: boolean;
}

const subscribers = new Set<Subscriber>();
let inForeground = true;
let registered = false;

// Calls `listener(true)` on every return to the foreground and `listener(false)` on every departure,
// until the returned function is called. Not deduplicated (see the header).
export function subscribePageForeground(
  listener: (foreground: boolean) => void,
  options: PageForegroundOptions = {},
): () => void {
  registerOnce();
  const subscriber: Subscriber = { listener, focus: options.focus === true };
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

// Whether the page is in the foreground as of the last signal a plain subscriber heard (a focus does
// not count, so this agrees with useIsAppActive()); `true` until the first one
export function isPageInForeground(): boolean {
  return inForeground;
}

// A return or a departure: every subscriber hears it, and isPageInForeground() records it
function deliver(foreground: boolean) {
  inForeground = foreground;
  // a copy, so a listener that subscribes another does not disturb this delivery
  for (const subscriber of [...subscribers]) {
    notify(subscriber, foreground);
  }
}

// A focus: only the subscribers that asked for it hear it, as a return, and the recorded state stays
// as it is (see PageForegroundOptions.focus)
function deliverFocus() {
  for (const subscriber of [...subscribers]) {
    if (subscriber.focus) {
      notify(subscriber, true);
    }
  }
}

function notify(subscriber: Subscriber, foreground: boolean) {
  // an earlier listener in this delivery may have unsubscribed this one
  if (!subscribers.has(subscriber)) {
    return;
  }
  try {
    subscriber.listener(foreground);
  } catch (error) {
    console.error('Page foreground: a listener failed', error);
  }
}

// The listeners go on at the first subscription rather than at import, so importing this module
// touches no globals
function registerOnce() {
  if (registered) {
    return;
  }
  registered = true;
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => deliver(document.visibilityState === 'visible'));
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', () => deliver(true));
    window.addEventListener('focus', deliverFocus);
  }
  registerNativeSignal();
}

// Old iOS app builds (before 4.17) push the app's foreground state into the page through
// globalThis.receiveOnAppDidBecomeActive, and may foreground without firing the browser's
// visibility events reliably. New builds no longer push it: Swift can't push reliably into the page
// under iOS 18 with limitsNavigationsToAppBoundDomains = true, and the visibilitychange and pageshow
// listeners above cover them.
// TODO(remove after 2027-01-01): once no old iOS apps are in the wild, delete this legacy push
// listener, as the visibilitychange/pageshow listeners above cover new iOS builds. With it go
// registerNativeSignal() and its call in registerOnce(), the native-signal bullet in this file's
// header, the receiveOnAppDidBecomeActive declaration in client/global.d.ts, the
// receiveOnAppDidBecomeActive calls in client/src/util/test/page_foreground.test.ts (and the values
// they add to its expected lists), and in client/src/audio/test/page_audio_context.test.ts the
// FakePage.signal() helper and the test "the native app-active signal is a return to the foreground".
// Its hits are counted under the [ios-native-foreground] tag in the application log.
function registerNativeSignal() {
  globalThis.receiveOnAppDidBecomeActive = (active?: boolean) => {
    console.log(`[ios-native-foreground] receiveOnAppDidBecomeActive(${active})`);
    deliver(active !== false);
  };
}
